import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtDate, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Gift, Plus, Sparkles, Trash2, BadgeCheck, Clock,
    Plane, Percent, Coffee, Fuel, Gem, TrendingUp, Film, Trophy,
    AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const TYPE_ICON = {
    "Cashback": Percent, "Discount": Percent, "Reward Points": Trophy,
    "Milestone Bonus": TrendingUp, "EMI": Percent, "Lounge Access": Plane,
    "Travel": Plane, "Dining": Coffee, "Fuel": Fuel, "Movie": Film,
    "Golf": Gem, "Insurance": BadgeCheck, "Concierge": Gem, "Other": Gift,
};

const TYPE_TONE = {
    "Cashback": "bg-emerald-50 text-emerald-800 border-emerald-200",
    "Discount": "bg-emerald-50 text-emerald-800 border-emerald-200",
    "Reward Points": "bg-amber-50 text-amber-900 border-amber-200",
    "Milestone Bonus": "bg-amber-50 text-amber-900 border-amber-200",
    "Lounge Access": "bg-sky-50 text-sky-800 border-sky-200",
    "Travel": "bg-sky-50 text-sky-800 border-sky-200",
    "Dining": "bg-rose-50 text-rose-800 border-rose-200",
    "Fuel": "bg-stone-100 text-stone-800 border-stone-200",
    "Movie": "bg-violet-50 text-violet-800 border-violet-200",
    "Golf": "bg-teal-50 text-teal-800 border-teal-200",
    "Insurance": "bg-indigo-50 text-indigo-800 border-indigo-200",
    "Concierge": "bg-teal-50 text-teal-800 border-teal-200",
    "EMI": "bg-stone-100 text-stone-800 border-stone-200",
    "Other": "bg-stone-100 text-stone-800 border-stone-200",
};

const EMPTY_FORM = {
    card_id: "", title: "", type: "Cashback", description: "",
    start_date: "", end_date: "", min_spend_inr: "", max_benefit_inr: "",
    coupon_code: "", applicable_merchant: "", recurring: false, renewal: "",
    remarks: "",
};

export default function Offers() {
    const { hasRole } = useAuth();
    const canEdit = hasRole && hasRole("admin", "finance");
    const [tab, setTab] = useState("live");
    const [rows, setRows] = useState([]);
    const [library, setLibrary] = useState([]);
    const [cards, setCards] = useState([]);
    const [summary, setSummary] = useState(null);
    const [openAdd, setOpenAdd] = useState(false);
    const [f, setF] = useState(EMPTY_FORM);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [o, l, c, s] = await Promise.all([
                api.get("/offers"),
                api.get("/offers/library"),
                api.get("/cards"),
                api.get("/offers/summary"),
            ]);
            setRows(o.data || []);
            setLibrary(l.data || []);
            setCards(c.data || []);
            setSummary(s.data || null);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const orphanRows = useMemo(() => rows.filter((o) => o.card_missing), [rows]);
    const cleanupOrphans = async () => {
        if (!window.confirm(
            `Archive ${orphanRows.length} orphaned offer${orphanRows.length === 1 ? "" : "s"} whose linked cards no longer exist? This cannot be undone from the UI.`,
        )) return;
        try {
            const { data } = await api.post("/offers/cleanup-orphans");
            toast.success(`Cleaned up ${data.archived} orphaned offer${data.archived === 1 ? "" : "s"}.`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const attach = async (entry) => {
        if (!entry.matching_cards?.length) {
            toast.info("None of your cards match this offer.");
            return;
        }
        try {
            await api.post("/offers/attach-from-library", {
                library_key: entry.library_key,
                card_id: entry.matching_cards[0].id,
            });
            toast.success(`Attached ${entry.title} → ${entry.matching_cards[0].label}`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const createOffer = async (e) => {
        e.preventDefault();
        if (!f.card_id || !f.title) {
            toast.error("Card and title are required.");
            return;
        }
        setBusy(true);
        try {
            const body = {
                ...f,
                min_spend_inr:   f.min_spend_inr   ? Number(f.min_spend_inr)   : null,
                max_benefit_inr: f.max_benefit_inr ? Number(f.max_benefit_inr) : null,
                start_date: f.start_date || null,
                end_date:   f.end_date   || null,
                renewal:    f.renewal    || null,
                source: "manual",
            };
            await api.post("/offers", body);
            toast.success("Offer added.");
            setOpenAdd(false);
            setF(EMPTY_FORM);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const markUsed = async (o) => {
        try {
            await api.post(`/offers/${o.id}/mark-used`);
            toast.success("Marked as used.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const archive = async (o) => {
        if (!window.confirm(`Remove "${o.title}" from ${o.card_label || "your card"}?`)) return;
        try {
            await api.delete(`/offers/${o.id}`);
            toast.info("Offer removed.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const liveRows   = useMemo(() => rows.filter((r) => !r.used && !r.expired), [rows]);
    const usedRows   = useMemo(() => rows.filter((r) => r.used || r.expired), [rows]);
    const librarySorted = useMemo(
        () => [...library].sort((a, b) => (b.matching_cards?.length || 0) - (a.matching_cards?.length || 0)),
        [library],
    );

    return (
        <div data-testid="offers-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Gift size={12} /> Money · Card benefits</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Offers & Benefits.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Every lounge visit, cashback tier and milestone bonus you should actually
                        use before it expires. Attach from a curated India library in one click.
                    </p>
                </div>
                {canEdit && (
                    <Button className="bg-stone-900 hover:bg-stone-800 text-white" onClick={() => setOpenAdd(true)}
                            data-testid="offer-add-btn">
                        <Plus size={13} className="mr-1.5" /> Add offer
                    </Button>
                )}
            </div>

            {orphanRows.length > 0 && hasRole("admin") && (
                <div className="mb-4 p-4 rounded-lg border border-red-200 bg-red-50/50 flex items-center gap-3 flex-wrap"
                     data-testid="orphan-offers-banner">
                    <AlertTriangle size={16} className="text-red-700 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-red-900">
                            {orphanRows.length} orphaned offer{orphanRows.length === 1 ? "" : "s"} · linked card no longer exists
                        </div>
                        <div className="text-[12px] text-red-800/80 mt-0.5">
                            These offers still show on Home under &ldquo;Use it or lose it&rdquo; but point at cards that were deleted. Clean them up in one click.
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        className="border-red-300 text-red-800 hover:bg-red-100 shrink-0"
                        onClick={cleanupOrphans}
                        data-testid="orphan-offers-cleanup-btn"
                    >
                        <Trash2 size={13} className="mr-1.5" /> Clean up orphaned offers
                    </Button>
                </div>
            )}

            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="offers-summary">
                    <SummaryCard label="Live offers" value={summary.live_offers} />
                    <SummaryCard label="Expiring in 30d" value={summary.expiring_in_30_days}
                                 tone={summary.expiring_in_30_days > 0 ? "amber" : "stone"} />
                    <SummaryCard label="Used" value={summary.used_offers} />
                    <SummaryCard label="Benefit YTD"
                                 value={fmtMoney(summary.benefit_received_ytd_inr, "INR")} mono />
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="offers-tabs">
                    <TabsTrigger value="live" data-testid="offers-tab-live">Live ({liveRows.length})</TabsTrigger>
                    <TabsTrigger value="library" data-testid="offers-tab-library">India library ({library.length})</TabsTrigger>
                    <TabsTrigger value="archive" data-testid="offers-tab-archive">Used / Expired ({usedRows.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="live" className="mt-4">
                    {liveRows.length === 0 ? (
                        <EmptyState title="No live offers yet"
                                    subtitle="Attach from the India library or add your own." />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="offers-live-list">
                            {liveRows.map((o) => (
                                <OfferCard key={o.id} o={o} canEdit={canEdit}
                                           onUsed={() => markUsed(o)} onArchive={() => archive(o)} />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="library" className="mt-4">
                    <div className="text-[12px] text-stone-500 mb-3">
                        Curated benefits for popular India cards. If you own one, we&rsquo;ve pre-matched it.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="offers-library-list">
                        {librarySorted.map((entry) => (
                            <LibraryCard key={entry.library_key} entry={entry}
                                         canAttach={canEdit && (entry.matching_cards?.length || 0) > 0}
                                         onAttach={() => attach(entry)} />
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="archive" className="mt-4">
                    {usedRows.length === 0 ? (
                        <EmptyState title="Nothing archived" subtitle="Used and expired offers show up here." />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {usedRows.map((o) => (
                                <OfferCard key={o.id} o={o} canEdit={canEdit}
                                           onUsed={() => markUsed(o)} onArchive={() => archive(o)} />
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Add offer dialog */}
            <Dialog open={openAdd} onOpenChange={setOpenAdd}>
                <DialogContent className="max-w-lg" data-testid="offer-add-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">New offer</DialogTitle>
                        <DialogDescription className="text-[13px] text-stone-500">
                            Track a bank promo, milestone or benefit tied to one of your cards.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={createOffer} className="space-y-3">
                        <div>
                            <Label className="eyebrow">Card *</Label>
                            <Select value={f.card_id} onValueChange={(v) => setF({ ...f, card_id: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="offer-form-card"><SelectValue placeholder="Choose card" /></SelectTrigger>
                                <SelectContent>
                                    {cards.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.bank_name} · {c.card_name} (••••{c.last_4})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <Label className="eyebrow">Title *</Label>
                                <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                                       className="mt-1.5" data-testid="offer-form-title"
                                       placeholder="e.g. 10x rewards on Smartbuy" />
                            </div>
                            <div>
                                <Label className="eyebrow">Type</Label>
                                <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.keys(TYPE_ICON).map((t) => (
                                            <SelectItem key={t} value={t}>{t}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="eyebrow">Renewal</Label>
                                <Select value={f.renewal || "none"} onValueChange={(v) => setF({ ...f, renewal: v === "none" ? "" : v })}>
                                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— none —</SelectItem>
                                        <SelectItem value="Monthly">Monthly</SelectItem>
                                        <SelectItem value="Quarterly">Quarterly</SelectItem>
                                        <SelectItem value="Yearly">Yearly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="eyebrow">Start</Label>
                                <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="mt-1.5" />
                            </div>
                            <div>
                                <Label className="eyebrow">End</Label>
                                <Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="mt-1.5" />
                            </div>
                            <div>
                                <Label className="eyebrow">Min spend ₹</Label>
                                <Input type="number" value={f.min_spend_inr} onChange={(e) => setF({ ...f, min_spend_inr: e.target.value })}
                                       className="mt-1.5 font-mono-figure" />
                            </div>
                            <div>
                                <Label className="eyebrow">Max benefit ₹</Label>
                                <Input type="number" value={f.max_benefit_inr} onChange={(e) => setF({ ...f, max_benefit_inr: e.target.value })}
                                       className="mt-1.5 font-mono-figure" />
                            </div>
                        </div>
                        <div>
                            <Label className="eyebrow">Description</Label>
                            <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1.5" />
                        </div>
                        <label className="flex items-center gap-2 text-[13px] text-stone-700 cursor-pointer">
                            <Checkbox checked={f.recurring} onCheckedChange={(v) => setF({ ...f, recurring: !!v })} />
                            Recurring benefit (e.g. every year)
                        </label>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpenAdd(false)}>Cancel</Button>
                            <Button type="submit" disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white"
                                    data-testid="offer-form-submit">
                                {busy ? "Saving…" : "Save offer"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function SummaryCard({ label, value, tone = "stone", mono = false }) {
    const toneClass = tone === "amber"
        ? "border-amber-200 bg-amber-50/50 text-amber-900"
        : "border-stone-200 bg-white text-stone-900";
    return (
        <div className={`rounded-xl border p-4 ${toneClass}`}>
            <div className="eyebrow">{label}</div>
            <div className={`font-display text-2xl leading-none mt-2 ${mono ? "font-mono-figure text-xl" : ""}`}>
                {value ?? "—"}
            </div>
        </div>
    );
}

function EmptyState({ title, subtitle }) {
    return (
        <div className="rounded-lg border border-dashed border-stone-300 bg-white p-14 text-center">
            <Gift size={20} className="mx-auto mb-3 text-stone-400" />
            <div className="text-sm text-stone-700">{title}</div>
            <div className="text-[12px] text-stone-500 mt-1.5">{subtitle}</div>
        </div>
    );
}

function OfferCard({ o, canEdit, onUsed, onArchive }) {
    const Icon = TYPE_ICON[o.type] || Gift;
    const tone = TYPE_TONE[o.type] || TYPE_TONE.Other;
    return (
        <div className="rounded-xl border border-stone-200 bg-white p-5 flex flex-col gap-3" data-testid={`offer-${o.id}`}>
            <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${tone}`}>
                    <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-900">{o.title}</div>
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 mt-0.5">
                        {o.type} {o.card_label ? `· ${o.card_label}` : ""}
                    </div>
                    {o.description && <div className="text-[12px] text-stone-600 mt-2 leading-relaxed">{o.description}</div>}
                </div>
                {o.expiring_soon && (
                    <div className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 flex items-center gap-1 shrink-0">
                        <Clock size={10} /> Expiring
                    </div>
                )}
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
                {o.min_spend_inr != null && (
                    <div><div className="uppercase tracking-widest text-stone-500">Min spend</div>
                        <div className="font-mono-figure text-stone-900">{fmtMoney(o.min_spend_inr)}</div></div>
                )}
                {o.max_benefit_inr != null && (
                    <div><div className="uppercase tracking-widest text-stone-500">Max benefit</div>
                        <div className="font-mono-figure text-stone-900">{fmtMoney(o.max_benefit_inr)}</div></div>
                )}
                {o.end_date && (
                    <div><div className="uppercase tracking-widest text-stone-500">Valid till</div>
                        <div className="text-stone-900">{fmtDate(o.end_date)}</div></div>
                )}
            </div>
            {canEdit && (
                <div className="flex items-center gap-2 pt-1 border-t border-stone-100">
                    {!o.used && (
                        <Button size="sm" onClick={onUsed} className="bg-emerald-700 hover:bg-emerald-800 text-white"
                                data-testid={`offer-mark-used-${o.id}`}>
                            <BadgeCheck size={12} className="mr-1" /> Mark used
                        </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={onArchive}
                            className="text-red-700 border-red-200 hover:bg-red-50 ml-auto"
                            data-testid={`offer-archive-${o.id}`}>
                        <Trash2 size={12} className="mr-1" /> Remove
                    </Button>
                </div>
            )}
        </div>
    );
}

function LibraryCard({ entry, canAttach, onAttach }) {
    const Icon = TYPE_ICON[entry.type] || Gift;
    const tone = TYPE_TONE[entry.type] || TYPE_TONE.Other;
    return (
        <div className={`rounded-xl border border-stone-200 bg-white p-4 flex items-start gap-3 ${
            canAttach ? "" : "opacity-70"
        }`} data-testid={`library-${entry.library_key}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${tone}`}>
                <Icon size={14} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-stone-900">{entry.title}</div>
                <div className="text-[11px] text-stone-500 mt-0.5">
                    {entry.bank} · {entry.card_match}
                </div>
                {entry.description && (
                    <div className="text-[12px] text-stone-600 mt-1.5 leading-relaxed line-clamp-2">
                        {entry.description}
                    </div>
                )}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {canAttach ? (
                        <>
                            <div className="text-[11px] text-stone-500">
                                Matches {entry.matching_cards.length} of your cards
                            </div>
                            <Button size="sm" variant="outline" onClick={onAttach}
                                    className="ml-auto" data-testid={`library-attach-${entry.library_key}`}>
                                <Sparkles size={11} className="mr-1" /> Attach
                            </Button>
                        </>
                    ) : (
                        <div className="text-[11px] text-stone-400 italic">
                            You don&rsquo;t have a matching card yet.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
